/**
 * Detalhe da empresa — dados cadastrais + gestão das 3 listas aninhadas:
 * unidades > setores > funções (selecione uma unidade pra ver os setores,
 * um setor pra ver as funções).
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useAtualizarEmpresa,
  useCriarFuncao,
  useCriarSetor,
  useCriarUnidade,
  useEmpresa,
  useFuncoes,
  useSetores,
  useUnidades,
} from "@/hooks/useApi";

export function EmpresaDetalhePage() {
  const { id } = useParams();
  const empresaId = id ? Number(id) : null;
  const { data: empresa, isLoading } = useEmpresa(empresaId);
  const atualizar = useAtualizarEmpresa();

  const [unidadeSel, setUnidadeSel] = useState<number | null>(null);
  const [setorSel, setSetorSel] = useState<number | null>(null);

  // Endereço da empresa: vai IMPRESSO no ASO. Não havia tela pra preencher —
  // empresa cadastrada pela importação do PCMSO sem endereço saía com "—" no
  // documento e não tinha como corrigir pelo app.
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ endereco: "", cidade: "", uf: "", cep: "" });

  if (isLoading) return <p className="text-ink-muted">Carregando…</p>;
  if (!empresa || empresaId === null)
    return <p className="text-sm text-ink-muted">Empresa não encontrada.</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold">{empresa.razao_social}</h2>
          <p className="text-sm text-ink-muted">
            CNPJ {empresa.cnpj} {empresa.cidade ? `· ${empresa.cidade}/${empresa.uf}` : ""}
            {empresa.grau_risco ? ` · grau de risco ${empresa.grau_risco}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {empresa.ativa ? (
            <span className="badge-ativo">ativa</span>
          ) : (
            <span className="badge-inativo">inativa</span>
          )}
          <button
            className="btn-secondary text-xs"
            disabled={atualizar.isPending}
            onClick={() => atualizar.mutate({ id: empresa.id, patch: { ativa: !empresa.ativa } })}
          >
            {empresa.ativa ? "Desativar" : "Reativar"}
          </button>
          <button
            className="btn-secondary text-xs"
            onClick={() => {
              setForm({
                endereco: empresa.endereco ?? "",
                cidade: empresa.cidade ?? "",
                uf: empresa.uf ?? "",
                cep: empresa.cep ?? "",
              });
              setEditando(true);
            }}
          >
            Editar endereço
          </button>
          <Link className="btn-secondary text-xs" to="/empresas">← Empresas</Link>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Endereço</h3>
          <span className="text-xs text-ink-muted">sai impresso no ASO</span>
        </div>
        {editando ? (
          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Endereço</label>
              <input className="input" value={form.endereco}
                placeholder="Rua, número, complemento, bairro"
                onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="w-56">
                <label className="mb-1 block text-xs font-medium text-ink-muted">Cidade</label>
                <input className="input" value={form.cidade}
                  onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
              </div>
              <div className="w-20">
                <label className="mb-1 block text-xs font-medium text-ink-muted">UF</label>
                <input className="input" maxLength={2} value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
              </div>
              <div className="w-32">
                <label className="mb-1 block text-xs font-medium text-ink-muted">CEP</label>
                <input className="input" maxLength={9} value={form.cep}
                  placeholder="00000-000"
                  onChange={(e) => setForm({ ...form, cep: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-primary" disabled={atualizar.isPending}
                onClick={async () => {
                  await atualizar.mutateAsync({
                    id: empresa.id,
                    patch: {
                      endereco: form.endereco.trim() || null,
                      cidade: form.cidade.trim() || null,
                      uf: form.uf.trim() || null,
                      cep: form.cep.trim() || null,
                    },
                  });
                  setEditando(false);
                }}>
                {atualizar.isPending ? "Salvando…" : "Salvar"}
              </button>
              <button className="btn-secondary" onClick={() => setEditando(false)}>Cancelar</button>
            </div>
            {atualizar.error ? (
              <p className="text-sm text-anarosa">{(atualizar.error as Error).message}</p>
            ) : null}
          </div>
        ) : empresa.endereco || empresa.cidade || empresa.cep ? (
          <p className="mt-2 text-sm">
            {empresa.endereco ?? "—"}
            {empresa.cidade ? ` · ${empresa.cidade}/${empresa.uf ?? "—"}` : ""}
            {empresa.cep ? ` · CEP ${empresa.cep}` : ""}
          </p>
        ) : (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Esta empresa <strong>não tem endereço cadastrado</strong>, então o ASO sai
            com “—” no campo Endereço. Clique em <strong>Editar endereço</strong> para
            preencher.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <UnidadesCard empresaId={empresaId} unidadeSel={unidadeSel}
          onSelect={(uid) => { setUnidadeSel(uid); setSetorSel(null); }} />
        <SetoresCard unidadeId={unidadeSel} setorSel={setorSel} onSelect={setSetorSel} />
        <FuncoesCard setorId={setorSel} />
      </div>
    </div>
  );
}

function UnidadesCard({ empresaId, unidadeSel, onSelect }: {
  empresaId: number;
  unidadeSel: number | null;
  onSelect: (id: number) => void;
}) {
  const { data, isLoading } = useUnidades(empresaId);
  const criar = useCriarUnidade();
  const [nome, setNome] = useState("");

  async function adicionar() {
    if (nome.trim().length < 2) return;
    try {
      await criar.mutateAsync({ empresaId, body: { nome: nome.trim() } });
      setNome("");
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="card p-4">
      <h3 className="font-display text-base font-semibold">Unidades</h3>
      <p className="text-xs text-ink-muted">Estabelecimentos / locais de lotação.</p>
      <div className="mt-3 flex gap-2">
        <input className="input" placeholder="nova unidade" value={nome}
          onChange={(e) => setNome(e.target.value)} />
        <button className="btn-primary" disabled={nome.trim().length < 2 || criar.isPending}
          onClick={adicionar}>+</button>
      </div>
      {criar.error ? <p className="mt-1 text-xs text-anarosa">{(criar.error as Error).message}</p> : null}
      {isLoading ? <p className="mt-3 text-sm text-ink-muted">Carregando…</p> : null}
      <ul className="mt-3 space-y-1">
        {(data ?? []).map((u) => (
          <li key={u.id}>
            <button
              className={`w-full rounded-md px-3 py-2 text-left text-sm ${
                unidadeSel === u.id ? "bg-anarosa text-white" : "hover:bg-anarosa-50"
              }`}
              onClick={() => onSelect(u.id)}
            >
              {u.nome}
              {u.contratante ? (
                <span className={unidadeSel === u.id ? "text-anarosa-100" : "text-ink-subtle"}>
                  {" "}· {u.contratante}
                </span>
              ) : null}
            </button>
          </li>
        ))}
        {data && data.length === 0 ? (
          <li className="text-sm text-ink-muted">Nenhuma unidade ainda.</li>
        ) : null}
      </ul>
    </div>
  );
}

function SetoresCard({ unidadeId, setorSel, onSelect }: {
  unidadeId: number | null;
  setorSel: number | null;
  onSelect: (id: number) => void;
}) {
  const { data, isLoading } = useSetores(unidadeId);
  const criar = useCriarSetor();
  const [nome, setNome] = useState("");

  async function adicionar() {
    if (unidadeId === null || !nome.trim()) return;
    try {
      await criar.mutateAsync({ unidadeId, body: { nome: nome.trim() } });
      setNome("");
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="card p-4">
      <h3 className="font-display text-base font-semibold">Setores</h3>
      <p className="text-xs text-ink-muted">
        {unidadeId === null ? "Selecione uma unidade." : "Setores da unidade selecionada."}
      </p>
      {unidadeId !== null ? (
        <>
          <div className="mt-3 flex gap-2">
            <input className="input" placeholder="novo setor" value={nome}
              onChange={(e) => setNome(e.target.value)} />
            <button className="btn-primary" disabled={!nome.trim() || criar.isPending}
              onClick={adicionar}>+</button>
          </div>
          {criar.error ? <p className="mt-1 text-xs text-anarosa">{(criar.error as Error).message}</p> : null}
          {isLoading ? <p className="mt-3 text-sm text-ink-muted">Carregando…</p> : null}
          <ul className="mt-3 space-y-1">
            {(data ?? []).map((s) => (
              <li key={s.id}>
                <button
                  className={`w-full rounded-md px-3 py-2 text-left text-sm ${
                    setorSel === s.id ? "bg-anarosa text-white" : "hover:bg-anarosa-50"
                  }`}
                  onClick={() => onSelect(s.id)}
                >
                  {s.nome}
                </button>
              </li>
            ))}
            {data && data.length === 0 ? (
              <li className="text-sm text-ink-muted">Nenhum setor ainda.</li>
            ) : null}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function FuncoesCard({ setorId }: { setorId: number | null }) {
  const { data, isLoading } = useFuncoes(setorId);
  const criar = useCriarFuncao();
  const [nome, setNome] = useState("");
  const [cbo, setCbo] = useState("");

  async function adicionar() {
    if (setorId === null || !nome.trim()) return;
    try {
      await criar.mutateAsync({ setorId, body: { nome: nome.trim(), cbo: cbo.trim() || null } });
      setNome("");
      setCbo("");
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="card p-4">
      <h3 className="font-display text-base font-semibold">Funções</h3>
      <p className="text-xs text-ink-muted">
        {setorId === null ? "Selecione um setor." : "Funções do setor selecionado."}
      </p>
      {setorId !== null ? (
        <>
          <div className="mt-3 flex gap-2">
            <input className="input" placeholder="nova função" value={nome}
              onChange={(e) => setNome(e.target.value)} />
            <input className="input w-24" placeholder="CBO" value={cbo}
              onChange={(e) => setCbo(e.target.value)} />
            <button className="btn-primary" disabled={!nome.trim() || criar.isPending}
              onClick={adicionar}>+</button>
          </div>
          {criar.error ? <p className="mt-1 text-xs text-anarosa">{(criar.error as Error).message}</p> : null}
          {isLoading ? <p className="mt-3 text-sm text-ink-muted">Carregando…</p> : null}
          <ul className="mt-3 space-y-1">
            {(data ?? []).map((f) => (
              <li key={f.id} className="rounded-md px-3 py-2 text-sm hover:bg-gray-50">
                {f.nome}
                {f.cbo ? <span className="text-ink-subtle"> · CBO {f.cbo}</span> : null}
              </li>
            ))}
            {data && data.length === 0 ? (
              <li className="text-sm text-ink-muted">Nenhuma função ainda.</li>
            ) : null}
          </ul>
        </>
      ) : null}
    </div>
  );
}
